import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AlertTriangle, ChevronDown, Clock, Download, Lock, RefreshCw, Link2, Users, Layers, Upload, ImageIcon } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_OPTIONS = ["Submitted", "In Production", "QC Check", "Delivered"];

const DAY_MS = 24 * 60 * 60 * 1000;
const CRITICAL_WINDOW_MS = 2 * 60 * 60 * 1000;

function easternOffsetMs(nowMs) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(nowMs);
  const g = (t) => Number(parts.find((p) => p.type === t).value);
  const asUTC = Date.UTC(g("year"), g("month") - 1, g("day"), g("hour") % 24, g("minute"), g("second"));
  return asUTC - nowMs;
}

function countdownInfo(brief, nowMs) {
  if (brief.status === "Delivered") return { ms: 0, critical: false, delivered: true };
  const wall = nowMs + easternOffsetMs(nowMs);
  const d = new Date(wall);
  const deadline = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 8, 0, 0);
  let ms = deadline - wall;
  if (ms <= 0) ms += DAY_MS;
  return { ms, critical: ms < CRITICAL_WINDOW_MS, delivered: false };
}

function formatCountdown(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const pad = (n) => String(n).padStart(2, "0");
  return `${Math.floor(total / 3600)}h ${pad(Math.floor((total % 3600) / 60))}m ${pad(total % 60)}s`;
}

const STATUS_DOTS = {
  Submitted: { dot: "bg-rose-400", ping: "bg-rose-400" },
  "In Production": { dot: "bg-amber-400", ping: "bg-amber-400" },
  "QC Check": { dot: "bg-sky-400", ping: "bg-sky-400" },
  Delivered: { dot: "bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.9)]", ping: null },
};

function StatusDot({ status, testid }) {
  const cfg = STATUS_DOTS[status] || STATUS_DOTS.Submitted;
  return (
    <span data-testid={testid} title={status} className="relative inline-flex h-2.5 w-2.5 shrink-0">
      {cfg.ping && (
        <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${cfg.ping}`} />
      )}
      <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${cfg.dot}`} />
    </span>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(sessionStorage.getItem("grapho_admin_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [briefs, setBriefs] = useState([]);
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [now, setNow] = useState(() => Date.now());
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newCredits, setNewCredits] = useState("");
  const [creating, setCreating] = useState(false);
  const [createMsg, setCreateMsg] = useState("");
  const [createErr, setCreateErr] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetMsg, setResetMsg] = useState("");
  const [resetErr, setResetErr] = useState("");

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const toggleCustomer = (email) => setExpanded((e) => ({ ...e, [email]: !e[email] }));

  const groups = useMemo(() => {
    const map = new Map();
    for (const b of briefs) {
      if (!map.has(b.customer_email)) {
        map.set(b.customer_email, { email: b.customer_email, customer: b.customer, briefs: [] });
      }
      map.get(b.customer_email).briefs.push(b);
    }
    const list = Array.from(map.values()).map((g) => {
      const orders = g.briefs.map((b) => ({ ...b, _cd: countdownInfo(b, now) }));
      orders.sort((a, z) => (a._cd.critical === z._cd.critical ? 0 : a._cd.critical ? -1 : 1));
      return { ...g, briefs: orders, hasCritical: orders.some((o) => o._cd.critical) };
    });
    list.sort((a, z) => (a.hasCritical === z.hasCritical ? 0 : a.hasCritical ? -1 : 1));
    return list;
  }, [briefs, now]);

  const authHeaders = () => ({ Authorization: `Bearer ${token}` });

  const loadBriefs = async (t = token) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/briefs`, { headers: { Authorization: `Bearer ${t}` } });
      setBriefs(data.briefs);
      setStats(data.stats);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to load briefs.");
      if (err.response?.status === 401) {
        setToken("");
        sessionStorage.removeItem("grapho_admin_token");
      }
    }
    setLoading(false);
  };

  const createAccount = async (e) => {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    setCreateMsg("");
    setCreateErr("");
    try {
      const { data } = await axios.post(
        `${API}/admin/create-account`,
        { email: newEmail.trim(), temp_password: newPassword, credits: Number(newCredits) },
        { headers: authHeaders() }
      );
      setCreateMsg(`Account created — ${data.email} starts with ${data.credits} design credits.`);
      setNewEmail("");
      setNewPassword("");
      setNewCredits("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setCreateErr(typeof detail === "string" ? detail : "Could not create the account.");
      if (err.response?.status === 401) {
        setToken("");
        sessionStorage.removeItem("grapho_admin_token");
      }
    }
    setCreating(false);
  };

  const resetAccountPassword = async (e) => {
    e.preventDefault();
    if (resetting) return;
    setResetting(true);
    setResetMsg("");
    setResetErr("");
    try {
      const { data } = await axios.post(
        `${API}/admin/reset-password`,
        { email: resetEmail.trim(), new_password: resetPassword },
        { headers: authHeaders() }
      );
      setResetMsg(`Password updated — ${data.email} can now sign in with the new password.`);
      setResetEmail("");
      setResetPassword("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setResetErr(typeof detail === "string" ? detail : "Could not reset the password.");
      if (err.response?.status === 401) {
        setToken("");
        sessionStorage.removeItem("grapho_admin_token");
      }
    }
    setResetting(false);
  };

  useEffect(() => {
    if (token) loadBriefs(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("grapho_admin_token", data.token);
      setToken(data.token);
      await loadBriefs(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed.");
    }
  };

  const changeStatus = async (briefId, status) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/briefs/${briefId}/status`,
        { status },
        { headers: authHeaders() }
      );
      if (status === "Delivered") {
        toast.success(
          data.email_status === "sent"
            ? "Marked Delivered — delivery email sent to the customer."
            : "Marked Delivered. Upload the completed PNG to trigger the delivery email."
        );
      } else {
        toast.success(`Status updated to ${status}.`);
      }
      await loadBriefs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update status.");
    }
  };

  const uploadFile = async (briefId, fileEl) => {
    const file = fileEl.files?.[0];
    if (!file) {
      toast.error("Choose a PNG file first.");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/admin/briefs/${briefId}/upload`, fd, { headers: authHeaders() });
      toast.success("Completed PNG attached.");
      await loadBriefs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    }
  };

  if (!token) {
    return (
      <div data-testid="admin-page" className="min-h-screen px-6 pb-20 pt-32">
        <form onSubmit={login} className="glass mx-auto max-w-md rounded-3xl p-8 text-center">
          <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-amber-400/25 bg-amber-400/10">
            <Lock className="h-5 w-5 text-amber-300" />
          </span>
          <h1 className="mt-5 text-2xl font-bold tracking-tight text-white">Admin Panel</h1>
          <p className="mt-2 text-sm text-slate-400">Studio access only — enter the admin password.</p>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            className="mt-6 w-full rounded-xl border border-white/10 bg-[#070B14]/80 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-sky-400/60 focus:outline-none focus:ring-2 focus:ring-sky-400/20"
          />
          <button
            data-testid="admin-login-button"
            type="submit"
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 px-6 py-3.5 text-sm font-bold text-[#1A1204] transition-all duration-300 hover:bg-amber-300"
          >
            <Lock className="h-4 w-4" />
            Unlock Admin Panel
          </button>
          {error && (
            <p data-testid="admin-error" className="mt-4 text-sm text-rose-400">
              {error}
            </p>
          )}
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen px-6 pb-20 pt-28">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-400">
              Studio admin // production queue
            </p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl">Incoming Briefs</h1>
          </div>
          <button
            data-testid="admin-refresh-button"
            onClick={() => loadBriefs()}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-200 transition-colors hover:border-sky-400/40"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <div data-testid="admin-stat-subscribers" className="glass flex items-center gap-4 rounded-2xl p-5">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-sky-400/25 bg-sky-400/10">
              <Users className="h-5 w-5 text-sky-400" />
            </span>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Active subscribers</p>
              <p className="text-3xl font-extrabold tracking-tight text-white">{stats ? stats.active_subscribers : "—"}</p>
            </div>
          </div>
          <div data-testid="admin-stat-queue" className="glass flex items-center gap-4 rounded-2xl p-5">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-amber-400/25 bg-amber-400/10">
              <Layers className="h-5 w-5 text-amber-300" />
            </span>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Orders in queue</p>
              <p className="text-3xl font-extrabold tracking-tight text-white">{stats ? stats.orders_in_queue : "—"}</p>
            </div>
          </div>
        </div>

        <form
          onSubmit={createAccount}
          data-testid="admin-create-account-card"
          className="glass mt-6 rounded-2xl p-5"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            Create buyer account
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-4">
            <input
              data-testid="admin-create-email-input"
              type="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="buyer@email.com"
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-sky-400/50 focus:outline-none"
            />
            <input
              data-testid="admin-create-password-input"
              type="text"
              required
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Temporary password (8+)"
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-sky-400/50 focus:outline-none"
            />
            <input
              data-testid="admin-create-credits-input"
              type="number"
              required
              min={0}
              value={newCredits}
              onChange={(e) => setNewCredits(e.target.value)}
              placeholder="Credit balance"
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-sky-400/50 focus:outline-none"
            />
            <button
              data-testid="admin-create-submit"
              type="submit"
              disabled={creating}
              className="rounded-xl bg-sky-400 px-4 py-2.5 text-sm font-bold text-[#070B14] transition hover:bg-sky-300 disabled:opacity-60"
            >
              {creating ? "Creating..." : "Create account"}
            </button>
          </div>
          {createMsg && (
            <p data-testid="admin-create-success" className="mt-3 text-sm text-emerald-400">
              {createMsg}
            </p>
          )}
          {createErr && (
            <p data-testid="admin-create-error" className="mt-3 text-sm text-rose-400">
              {createErr}
            </p>
          )}
        </form>

        <form
          onSubmit={resetAccountPassword}
          data-testid="admin-reset-password-card"
          className="glass mt-4 rounded-2xl p-5"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            Reset account password
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <input
              data-testid="admin-reset-email-input"
              type="email"
              required
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              placeholder="Existing account email"
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-sky-400/50 focus:outline-none"
            />
            <input
              data-testid="admin-reset-password-input"
              type="text"
              required
              minLength={8}
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              placeholder="New password (8+)"
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-sky-400/50 focus:outline-none"
            />
            <button
              data-testid="admin-reset-submit"
              type="submit"
              disabled={resetting}
              className="rounded-xl border border-sky-400/40 bg-sky-400/10 px-4 py-2.5 text-sm font-bold text-sky-300 transition hover:bg-sky-400/20 disabled:opacity-60"
            >
              {resetting ? "Saving..." : "Save new password"}
            </button>
          </div>
          {resetMsg && (
            <p data-testid="admin-reset-success" className="mt-3 text-sm text-emerald-400">
              {resetMsg}
            </p>
          )}
          {resetErr && (
            <p data-testid="admin-reset-error" className="mt-3 text-sm text-rose-400">
              {resetErr}
            </p>
          )}
        </form>

        {error && (
          <p data-testid="admin-list-error" className="mt-6 text-sm text-rose-400">
            {error}
          </p>
        )}

        {briefs.length === 0 && !error ? (
          <p data-testid="admin-empty" className="mt-8 rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-slate-500">
            No briefs in the queue yet.
          </p>
        ) : (
          <div data-testid="admin-briefs-list" className="mt-8 space-y-4">
            {groups.map((g, gi) => {
              const isOpen = !!expanded[g.email];
              return (
                <div
                  key={g.email}
                  data-testid={`admin-customer-card-${gi}`}
                  className="rounded-2xl border border-white/10 bg-[#0C1222]/80"
                >
                  <button
                    data-testid={`admin-customer-toggle-${gi}`}
                    onClick={() => toggleCustomer(g.email)}
                    aria-expanded={isOpen}
                    className="flex w-full flex-wrap items-center gap-3 p-5 text-left"
                  >
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 text-slate-500 transition-transform duration-300 ${isOpen ? "rotate-0" : "-rotate-90"}`}
                    />
                    <span
                      data-testid={`admin-customer-email-${gi}`}
                      className="min-w-0 flex-1 truncate text-sm font-bold text-white"
                    >
                      {g.email}
                    </span>
                    <span className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                      {g.customer ? (
                        <>
                          <span
                            data-testid={`admin-customer-plan-${gi}`}
                            className="rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-1 text-[11px] font-semibold text-amber-300"
                          >
                            {g.customer.plan_name}
                          </span>
                          <span
                            data-testid={`admin-customer-credits-${gi}`}
                            className="rounded-full border border-sky-400/30 bg-sky-400/10 px-2.5 py-1 text-[11px] font-semibold text-sky-300"
                          >
                            {g.customer.credits_remaining} credits left
                          </span>
                          {g.customer.plan_status === "cancelled" && (
                            <span className="rounded-full border border-rose-400/40 bg-rose-400/10 px-2.5 py-1 text-[11px] font-semibold text-rose-300">
                              Cancelled
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-slate-400">
                          No account record
                        </span>
                      )}
                      <span
                        data-testid={`admin-customer-order-count-${gi}`}
                        className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-slate-400"
                      >
                        {g.briefs.length} order{g.briefs.length === 1 ? "" : "s"}
                      </span>
                    </span>
                  </button>

                  {isOpen && (
                    <div className="space-y-4 border-t border-white/10 p-5">
                      {g.briefs.map((b) => (
                        <div
                          key={b.brief_id}
                          data-testid={`admin-brief-card-${b.brief_id}`}
                          className="rounded-xl border border-white/10 bg-[#070B14]/60 p-4"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                              Order {b.brief_id.slice(0, 8)}
                            </span>
                            <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                              {new Date(b.created_at).toLocaleString("en-US", {
                                month: "short",
                                day: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                              })}
                            </span>
                          </div>

                          <p className="mt-3 text-sm leading-relaxed text-slate-200">{b.request}</p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {b.niche && <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-slate-300">Niche: {b.niche}</span>}
                            {b.theme && <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-slate-300">Theme: {b.theme}</span>}
                            {b.style && <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-slate-300">Style: {b.style}</span>}
                          </div>
                          {b.reference_links && (
                            <p data-testid={`admin-inspiration-links-${b.brief_id}`} className="mt-2 text-xs leading-relaxed text-slate-400">
                              <span className="font-semibold text-slate-300">Inspiration links: </span>
                              {b.reference_links
                                .split(/[\s,]+/)
                                .filter(Boolean)
                                .map((l, li) =>
                                  /^https?:\/\//i.test(l) ? (
                                    <a key={li} href={l} target="_blank" rel="noopener noreferrer" className="mr-2 break-all text-sky-400 hover:text-sky-300">
                                      {l}
                                    </a>
                                  ) : (
                                    <span key={li} className="mr-2 break-all">{l}</span>
                                  )
                                )}
                            </p>
                          )}

                          {b.items && b.items.length > 0 && (
                            <div className="mt-3 space-y-2 rounded-xl border border-white/5 bg-[#0C1222]/60 p-3">
                              {b.items.map((it, ii) => (
                                <div key={ii} className="flex items-start gap-3">
                                  {it.ref_path ? (
                                    <div className="relative shrink-0">
                                      <a
                                        data-testid={`admin-ref-link-${b.brief_id}-${ii}`}
                                        href={`${API}/files/${it.ref_path}?inline=1`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Open full reference image in a new tab"
                                        className="block"
                                      >
                                        <img
                                          src={`${API}/files/${it.ref_path}`}
                                          alt={`Reference ${ii + 1}`}
                                          className="h-12 w-12 rounded-lg border border-white/10 object-cover transition-transform duration-200 hover:scale-110"
                                        />
                                      </a>
                                      <a
                                        data-testid={`admin-ref-download-${b.brief_id}-${ii}`}
                                        href={`${API}/files/${it.ref_path}`}
                                        download
                                        title="Download reference image"
                                        className="absolute -bottom-1.5 -right-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full border border-white/15 bg-[#0C1222] text-slate-300 transition-colors hover:border-sky-400/50 hover:text-sky-300"
                                      >
                                        <Download className="h-3 w-3" />
                                      </a>
                                    </div>
                                  ) : (
                                    <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5">
                                      <ImageIcon className="h-4 w-4 text-slate-600" />
                                    </span>
                                  )}
                                  <div className="min-w-0">
                                    <p className="text-[11px] font-semibold text-slate-300">Design {ii + 1}</p>
                                    <p className="truncate text-xs text-slate-400">{it.note || "No notes — artist's choice"}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {b.status !== "Delivered" && (
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              <span
                                data-testid={`admin-countdown-${b.brief_id}`}
                                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[11px] font-semibold ${
                                  b._cd.critical
                                    ? "border-rose-400/50 bg-rose-400/10 text-rose-300"
                                    : "border-white/10 bg-white/5 text-slate-300"
                                }`}
                              >
                                <Clock className="h-3 w-3" />
                                {formatCountdown(b._cd.ms)} to 8 AM EST deadline
                              </span>
                              {b._cd.critical && (
                                <span
                                  data-testid={`admin-critical-warning-${b.brief_id}`}
                                  className="critical-flash inline-flex items-center gap-1.5 rounded-full border border-rose-500/60 bg-rose-500/15 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-rose-300"
                                >
                                  <AlertTriangle className="h-3.5 w-3.5" />
                                  Critical deadline warning
                                </span>
                              )}
                            </div>
                          )}

                          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-white/5 pt-4">
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</label>
                            <StatusDot status={b.status} testid={`admin-status-dot-${b.brief_id}`} />
                            <select
                              data-testid={`admin-status-select-${b.brief_id}`}
                              value={b.status}
                              onChange={(e) => changeStatus(b.brief_id, e.target.value)}
                              className="rounded-lg border border-white/10 bg-[#070B14] px-3 py-2 text-xs font-semibold text-slate-100 focus:border-sky-400/60 focus:outline-none"
                            >
                              {STATUS_OPTIONS.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>

                            <label
                              htmlFor={`file-${b.brief_id}`}
                              className="ml-auto cursor-pointer rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold text-slate-200 transition-colors hover:border-sky-400/40"
                            >
                              <Upload className="mr-1.5 inline h-3.5 w-3.5" />
                              Attach completed PNG
                            </label>
                            <input
                              id={`file-${b.brief_id}`}
                              data-testid={`admin-upload-input-${b.brief_id}`}
                              type="file"
                              accept="image/png"
                              className="hidden"
                              onChange={(e) => uploadFile(b.brief_id, e.target)}
                            />

                            {b.delivered_path && (
                              <a
                                data-testid={`admin-delivered-link-${b.brief_id}`}
                                href={`${API}/files/${b.delivered_path}`}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 hover:text-emerald-300"
                              >
                                <Link2 className="h-3.5 w-3.5" />
                                {b.delivered_filename || "Delivered file"}
                              </a>
                            )}
                            {b.status === "Delivered" && b.email_status && (
                              <span
                                data-testid={`admin-email-status-${b.brief_id}`}
                                className={`text-xs font-semibold ${b.email_status === "sent" ? "text-emerald-400" : "text-rose-400"}`}
                              >
                                {b.email_status === "sent" ? "Delivery email sent" : "Email pending / failed"}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}